
// frontend/src/components/Sports/UpcomingFixtures.js
// Shows scheduled upcoming matches with date grouping
import React from 'react';
import { motion } from 'framer-motion';
import { useSportsScores } from '../../services/SportsDataService';
import { Calendar, Clock, Tv } from 'lucide-react';

// Helper to format date
const formatMatchDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow';

    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
};

// Group matches by date
const groupByDate = (games) => {
    const groups = {};
    games.forEach(game => {
        const dateKey = new Date(game.date).toDateString();
        if (!groups[dateKey]) {
            groups[dateKey] = {
                label: formatMatchDate(game.date),
                matches: []
            };
        }
        groups[dateKey].matches.push(game);
    });
    return Object.values(groups);
};

const FixtureRow = ({ event }) => {
    const competition = event.competitions[0];
    const status = competition.status.type;
    const home = competition.competitors.find(c => c.homeAway === 'home');
    const away = competition.competitors.find(c => c.homeAway === 'away');

    const isLive = status.state === 'in';
    const isCompleted = status.state === 'post';

    return (
        <div className="flex items-center justify-between p-3 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors">
            {/* Teams */}
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                    <img src={home?.team?.logo} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                    <span className="text-sm font-medium text-white truncate">{home?.team?.abbreviation}</span>
                </div>

                <div className="flex-shrink-0 px-3">
                    {isLive ? (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded animate-pulse">
                            {home?.score} - {away?.score}
                        </span>
                    ) : isCompleted ? (
                        <span className="text-sm text-muted-foreground">
                            {home?.score} - {away?.score}
                        </span>
                    ) : (
                        <span className="text-xs text-muted-foreground">vs</span>
                    )}
                </div>

                <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                    <span className="text-sm font-medium text-white truncate">{away?.team?.abbreviation}</span>
                    <img src={away?.team?.logo} alt="" className="w-6 h-6 object-contain flex-shrink-0" />
                </div>
            </div>

            {/* Time / Status */}
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                {isLive ? (
                    <span className="flex items-center gap-1 text-xs text-red-400 font-bold">
                        <Tv className="w-3 h-3" />
                        LIVE
                    </span>
                ) : isCompleted ? (
                    <span className="text-xs text-muted-foreground">FT</span>
                ) : (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {formatTime(event.date)}
                    </span>
                )}
            </div>
        </div>
    );
};

export const UpcomingFixtures = ({ sport, league, limit = 10 }) => {
    const { games, isLoading, isError } = useSportsScores(sport, league);

    if (isLoading) {
        return (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Calendar className="w-5 h-5 text-primary" />
                    <div className="h-5 w-32 bg-white/10 rounded animate-pulse" />
                </div>
                <div className="space-y-2">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-12 bg-white/5 rounded animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (isError || games.length === 0) {
        return (
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
                <Calendar className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">No upcoming fixtures</p>
            </div>
        );
    }

    // Only show upcoming (pre) or in-progress games
    const upcomingGames = games
        .filter(g => g.competitions[0].status.type.state !== 'post')
        .slice(0, limit);

    const grouped = groupByDate(upcomingGames);

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl border border-white/10 overflow-hidden"
            style={{
                background: 'rgba(255, 255, 255, 0.02)',
                backdropFilter: 'blur(12px)',
            }}
        >
            {/* Header */}
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
                <Calendar className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-bold text-white">Upcoming Fixtures</h3>
            </div>

            {/* Fixtures by Date */}
            <div>
                {grouped.map((group, i) => (
                    <div key={i}>
                        {/* Date Header */}
                        <div className="px-4 py-2 bg-white/5 border-b border-white/5">
                            <span className="text-xs font-bold text-primary uppercase tracking-wider">
                                {group.label}
                            </span>
                        </div>
                        {/* Matches */}
                        {group.matches.map((match, j) => (
                            <FixtureRow key={match.id || j} event={match} />
                        ))}
                    </div>
                ))}
            </div>
        </motion.div>
    );
};
